import type { Box, ResolvedFigure, SceneShape } from './model.js';

export type SemanticAssertion =
  | { type: 'pointsTo'; figure: string; from: string; to: string }
  | { type: 'spans'; figure: string; span: string; members: string[]; axis?: 'x' | 'y'; tolerance?: number }
  | { type: 'contains'; figure: string; container: string; members: string[]; tolerance?: number }
  | { type: 'ordered'; figure: string; semanticIds: string[]; axis: 'x' | 'y'; direction?: 'ascending' | 'descending' }
  | { type: 'routesThrough'; figure: string; semanticIds: string[]; relationType?: string }
  | { type: 'unchangedAcross'; fromFigure: string; toFigure: string; semanticIds: string[]; tolerance?: number }
  | { type: 'touchable'; figure: string; semanticIds: string[]; minimum?: number }
  | { type: 'anchoredTo'; figure: string; anchorId: string; semanticId: string }
  | { type: 'noOverlap'; figure: string; semanticIds: string[]; padding?: number };

export interface SemanticGateFailure {
  assertionIndex: number;
  assertionType: SemanticAssertion['type'];
  message: string;
}

export interface SemanticGateResult {
  ok: boolean;
  checked: number;
  passed: number;
  failures: SemanticGateFailure[];
}

export const pointsTo = (figure: string, from: string, to: string): SemanticAssertion => ({ type: 'pointsTo', figure, from, to });
export const spans = (figure: string, span: string, members: string[], axis: 'x' | 'y' = 'x', tolerance = 1): SemanticAssertion => ({ type: 'spans', figure, span, members, axis, tolerance });
export const contains = (figure: string, container: string, members: string[], tolerance = 1): SemanticAssertion => ({ type: 'contains', figure, container, members, tolerance });
export const ordered = (figure: string, semanticIds: string[], axis: 'x' | 'y', direction: 'ascending' | 'descending' = 'ascending'): SemanticAssertion => ({ type: 'ordered', figure, semanticIds, axis, direction });
export const routesThrough = (figure: string, semanticIds: string[], relationType?: string): SemanticAssertion => ({ type: 'routesThrough', figure, semanticIds, ...(relationType ? { relationType } : {}) });
export const unchangedAcross = (fromFigure: string, toFigure: string, semanticIds: string[], tolerance = 0): SemanticAssertion => ({ type: 'unchangedAcross', fromFigure, toFigure, semanticIds, tolerance });
export const touchable = (figure: string, semanticIds: string[], minimum = 44): SemanticAssertion => ({ type: 'touchable', figure, semanticIds, minimum });
export const anchoredTo = (figure: string, anchorId: string, semanticId: string): SemanticAssertion => ({ type: 'anchoredTo', figure, anchorId, semanticId });
export const noOverlap = (figure: string, semanticIds: string[], padding = 0): SemanticAssertion => ({ type: 'noOverlap', figure, semanticIds, padding });

/** Run claim-level checks against resolved, addressable scene graphs. */
export function runSemanticGate(
  figures: Readonly<Record<string, ResolvedFigure>>,
  assertions: readonly SemanticAssertion[],
): SemanticGateResult {
  const failures: SemanticGateFailure[] = [];
  assertions.forEach((assertion, assertionIndex) => {
    try {
      evaluate(figures, assertion);
    } catch (error) {
      failures.push({
        assertionIndex,
        assertionType: assertion.type,
        message: error instanceof Error ? error.message : String(error),
      });
    }
  });
  return { ok: failures.length === 0, checked: assertions.length, passed: assertions.length - failures.length, failures };
}

export function assertSemanticGate(
  figures: Readonly<Record<string, ResolvedFigure>>,
  assertions: readonly SemanticAssertion[],
  label = 'semantic gate',
): SemanticGateResult {
  const result = runSemanticGate(figures, assertions);
  if (!result.ok) {
    throw new Error(`${label} failed (${result.passed}/${result.checked} passed):\n${result.failures.map((failure) => `  [${failure.assertionIndex}] ${failure.assertionType}: ${failure.message}`).join('\n')}`);
  }
  return result;
}

function evaluate(figures: Readonly<Record<string, ResolvedFigure>>, assertion: SemanticAssertion): void {
  if (assertion.type === 'unchangedAcross') {
    const before = getFigure(figures, assertion.fromFigure);
    const after = getFigure(figures, assertion.toFigure);
    for (const semanticId of nonEmpty(assertion.semanticIds, 'unchangedAcross semanticIds')) {
      const beforeGeometry = geometryFor(before, semanticId);
      const afterGeometry = geometryFor(after, semanticId);
      if (beforeGeometry.length !== afterGeometry.length) {
        fail(`“${semanticId}” changed shape count from ${beforeGeometry.length} in “${assertion.fromFigure}” to ${afterGeometry.length} in “${assertion.toFigure}”`);
      }
      for (let index = 0; index < beforeGeometry.length; index += 1) {
        if (!sameGeometry(beforeGeometry[index]!, afterGeometry[index]!, assertion.tolerance ?? 0)) {
          fail(`“${semanticId}” moved or resized between “${assertion.fromFigure}” and “${assertion.toFigure}” at shape ${beforeGeometry[index]!.id}`);
        }
      }
    }
    return;
  }

  const figure = getFigure(figures, assertion.figure);
  if (assertion.type === 'pointsTo') {
    requireSemantic(figure, assertion.from);
    requireSemantic(figure, assertion.to);
    if (!figure.relations.some((relation) => relation.type === 'points-to' && relation.from === assertion.from && relation.to === assertion.to)) {
      fail(`“${assertion.from}” does not have a points-to relation terminating at “${assertion.to}” in “${assertion.figure}”`);
    }
    return;
  }

  if (assertion.type === 'spans') {
    const spanBounds = boundsFor(figure, assertion.span);
    const memberBounds = union(nonEmpty(assertion.members, 'spans members').map((id) => boundsFor(figure, id)));
    const tolerance = assertion.tolerance ?? 1;
    const spanStart = assertion.axis === 'y' ? spanBounds.y : spanBounds.x;
    const spanEnd = assertion.axis === 'y' ? spanBounds.y + spanBounds.height : spanBounds.x + spanBounds.width;
    const memberStart = assertion.axis === 'y' ? memberBounds.y : memberBounds.x;
    const memberEnd = assertion.axis === 'y' ? memberBounds.y + memberBounds.height : memberBounds.x + memberBounds.width;
    if (Math.abs(spanStart - memberStart) > tolerance || Math.abs(spanEnd - memberEnd) > tolerance) {
      fail(`“${assertion.span}” spans ${format(spanStart)}…${format(spanEnd)}, not the ${format(memberStart)}…${format(memberEnd)} extent of [${assertion.members.join(', ')}] on ${assertion.axis ?? 'x'}`);
    }
    return;
  }

  if (assertion.type === 'contains') {
    const outer = boundsFor(figure, assertion.container);
    const tolerance = assertion.tolerance ?? 1;
    for (const id of nonEmpty(assertion.members, 'contains members')) {
      const inner = boundsFor(figure, id);
      if (!boxContains(outer, inner, tolerance)) fail(`“${assertion.container}” ${boxText(outer)} does not contain “${id}” ${boxText(inner)}`);
    }
    return;
  }

  if (assertion.type === 'ordered') {
    const ids = nonEmpty(assertion.semanticIds, 'ordered semanticIds');
    const values = ids.map((id) => center(boundsFor(figure, id))[assertion.axis]);
    const sign = assertion.direction === 'descending' ? -1 : 1;
    for (let index = 1; index < values.length; index += 1) {
      if ((values[index]! - values[index - 1]!) * sign <= 0) {
        fail(`[${ids.join(', ')}] is not strictly ${assertion.direction ?? 'ascending'} on ${assertion.axis}; “${ids[index - 1]}”=${format(values[index - 1]!)} then “${ids[index]}”=${format(values[index]!)}`);
      }
    }
    return;
  }

  if (assertion.type === 'routesThrough') {
    const ids = nonEmpty(assertion.semanticIds, 'routesThrough semanticIds');
    ids.forEach((id) => requireSemantic(figure, id));
    for (let index = 1; index < ids.length; index += 1) {
      const from = ids[index - 1]!;
      const to = ids[index]!;
      const relation = figure.relations.find((candidate) => candidate.from === from && candidate.to === to && (!assertion.relationType || candidate.type === assertion.relationType));
      if (!relation) fail(`route breaks between “${from}” and “${to}”; expected a${assertion.relationType ? ` “${assertion.relationType}”` : ''} relation`);
    }
    return;
  }

  if (assertion.type === 'touchable') {
    const minimum = assertion.minimum ?? 44;
    for (const semanticId of nonEmpty(assertion.semanticIds, 'touchable semanticIds')) {
      const regions = figure.hitRegions.filter((region) => region.semanticId === semanticId);
      if (!regions.length) fail(`“${semanticId}” has no hit region in “${assertion.figure}”`);
      if (!regions.some((region) => region.box.width >= minimum && region.box.height >= minimum)) {
        fail(`“${semanticId}” has no ${minimum}×${minimum} touch target; largest is ${largestRegion(regions.map((region) => region.box))}`);
      }
    }
    return;
  }

  if (assertion.type === 'anchoredTo') {
    requireSemantic(figure, assertion.semanticId);
    const anchor = figure.anchors.find((candidate) => candidate.id === assertion.anchorId);
    if (!anchor) fail(`anchor “${assertion.anchorId}” does not exist in “${assertion.figure}”`);
    if (anchor.semanticId !== assertion.semanticId) fail(`anchor “${assertion.anchorId}” belongs to “${anchor.semanticId}”, not “${assertion.semanticId}”`);
    return;
  }

  const ids = nonEmpty(assertion.semanticIds, 'noOverlap semanticIds');
  const boxes = ids.map((id) => ({ id, box: primaryBoundsFor(figure, id) }));
  for (let left = 0; left < boxes.length; left += 1) {
    for (let right = left + 1; right < boxes.length; right += 1) {
      if (overlaps(boxes[left]!.box, boxes[right]!.box, assertion.padding ?? 0)) {
        fail(`“${boxes[left]!.id}” ${boxText(boxes[left]!.box)} overlaps “${boxes[right]!.id}” ${boxText(boxes[right]!.box)}`);
      }
    }
  }
}

function getFigure(figures: Readonly<Record<string, ResolvedFigure>>, id: string): ResolvedFigure {
  const figure = figures[id];
  if (!figure) fail(`figure state “${id}” is missing; available states: ${Object.keys(figures).sort().join(', ') || '(none)'}`);
  return figure;
}

function semanticIds(figure: ResolvedFigure): Set<string> {
  return new Set([
    ...figure.shapes.map((shape) => shape.semanticId),
    ...figure.anchors.map((anchor) => anchor.semanticId),
    ...figure.hitRegions.map((region) => region.semanticId),
    ...figure.relations.flatMap((relation) => [relation.from, relation.to]),
  ]);
}

function requireSemantic(figure: ResolvedFigure, id: string): void {
  if (!semanticIds(figure).has(id)) fail(`semantic object “${id}” does not exist in resolved figure “${figure.id}”`);
}

function boundsFor(figure: ResolvedFigure, semanticId: string): Box {
  const shapes = figure.shapes.filter((shape) => shape.semanticId === semanticId && shape.kind !== 'text');
  if (!shapes.length) fail(`semantic object “${semanticId}” has no structural geometry in resolved figure “${figure.id}”`);
  // A front rect is the canonical extent when a compiler adds decorative depth polygons.
  const rects = shapes.filter((shape) => shape.kind === 'rect');
  return union((rects.length ? rects : shapes).map(shapeBounds));
}

function primaryBoundsFor(figure: ResolvedFigure, semanticId: string): Box {
  const hits = figure.hitRegions.filter((region) => region.semanticId === semanticId);
  if (hits.length) return union(hits.map((hit) => hit.box));
  return boundsFor(figure, semanticId);
}

function shapeBounds(shape: SceneShape): Box {
  if (shape.kind === 'rect') return shape.box;
  if (shape.kind === 'line') return fromPoints([shape.from, shape.to]);
  if (shape.kind === 'polygon') return fromPoints(shape.points);
  if (shape.kind === 'text') {
    const width = shape.text.length * shape.size * 0.6;
    const x = shape.align === 'middle' ? shape.at.x - width / 2 : shape.align === 'end' ? shape.at.x - width : shape.at.x;
    return { x, y: shape.at.y - shape.size, width, height: shape.size * 1.2 };
  }
  const numbers = [...shape.d.matchAll(/-?\d+(?:\.\d+)?/g)].map((match) => Number(match[0]));
  const points: Array<{ x: number; y: number }> = [];
  for (let index = 0; index + 1 < numbers.length; index += 2) points.push({ x: numbers[index]!, y: numbers[index + 1]! });
  if (!points.length) fail(`path shape “${shape.id}” has no measurable coordinates`);
  return fromPoints(points);
}

function fromPoints(points: Array<{ x: number; y: number }>): Box {
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
}

function union(boxes: Box[]): Box {
  const x = Math.min(...boxes.map((item) => item.x));
  const y = Math.min(...boxes.map((item) => item.y));
  return {
    x,
    y,
    width: Math.max(...boxes.map((item) => item.x + item.width)) - x,
    height: Math.max(...boxes.map((item) => item.y + item.height)) - y,
  };
}

interface ShapeGeometry { id: string; numbers: number[] }

function geometryFor(figure: ResolvedFigure, semanticId: string): ShapeGeometry[] {
  requireSemantic(figure, semanticId);
  return figure.shapes
    .filter((shape) => shape.semanticId === semanticId)
    .map((shape) => ({ id: shape.id, numbers: geometryNumbers(shape) }))
    .sort((a, b) => a.id.localeCompare(b.id));
}

function geometryNumbers(shape: SceneShape): number[] {
  if (shape.kind === 'rect') return [shape.box.x, shape.box.y, shape.box.width, shape.box.height, shape.radius ?? 0];
  if (shape.kind === 'line') return [shape.from.x, shape.from.y, shape.to.x, shape.to.y];
  if (shape.kind === 'polygon') return shape.points.flatMap((point) => [point.x, point.y]);
  if (shape.kind === 'text') return [shape.at.x, shape.at.y, shape.size, shape.rotate ?? 0];
  return [...shape.d.matchAll(/-?\d+(?:\.\d+)?/g)].map((match) => Number(match[0]));
}

function sameGeometry(left: ShapeGeometry, right: ShapeGeometry, tolerance: number): boolean {
  return left.id === right.id && left.numbers.length === right.numbers.length && left.numbers.every((value, index) => Math.abs(value - right.numbers[index]!) <= tolerance);
}

function boxContains(outer: Box, inner: Box, tolerance: number): boolean {
  return inner.x >= outer.x - tolerance && inner.y >= outer.y - tolerance
    && inner.x + inner.width <= outer.x + outer.width + tolerance
    && inner.y + inner.height <= outer.y + outer.height + tolerance;
}

function overlaps(left: Box, right: Box, padding: number): boolean {
  return left.x < right.x + right.width + padding && left.x + left.width + padding > right.x
    && left.y < right.y + right.height + padding && left.y + left.height + padding > right.y;
}

function center(item: Box): { x: number; y: number } {
  return { x: item.x + item.width / 2, y: item.y + item.height / 2 };
}

function nonEmpty<T>(items: readonly T[], label: string): readonly T[] {
  if (!items.length) fail(`${label} must contain at least one item`);
  return items;
}

function largestRegion(boxes: Box[]): string {
  const largest = [...boxes].sort((a, b) => b.width * b.height - a.width * a.height)[0]!;
  return `${format(largest.width)}×${format(largest.height)}`;
}

function format(value: number): string { return String(Math.round(value * 100) / 100); }
function boxText(item: Box): string { return `[${format(item.x)}, ${format(item.y)}, ${format(item.width)}×${format(item.height)}]`; }
function fail(message: string): never { throw new Error(message); }
